import { IconSearch } from "@tabler/icons-react";
import { useMemo, useState, type KeyboardEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { COMANDOS, type Comando } from "@/components/ui/command-palette/data/comandos";
import { useCommandPaletteShortcut } from "@/components/ui/command-palette/hooks/use-command-palette-shortcut";
import { comandosDisponiblesPara, filtrarPorTexto } from "@/components/ui/command-palette/utils/filtrar-comandos";
import { useUserPermisos, useUserRoles } from "@/lib/auth-store";
import { Dialog, DialogContent } from "../Dialog";
import { CommandPaletteFooter } from "./CommandPaletteFooter";
import { CommandPaletteItem } from "./CommandPaletteItem";

/**
 * Paleta de comandos global (`Ctrl+K`/`Cmd+K`) · reemplaza el `Modal` de
 * Mantine por `Dialog` (Radix, Etapa 2) — Radix ya cierra con Escape de
 * forma nativa, así que a diferencia del original no hace falta agregar
 * ese handler a mano. `data/comandos.ts`, `hooks/use-command-palette-
 * shortcut.ts` y `utils/filtrar-comandos.ts` (en `command-palette/`, la
 * carpeta de la versión Mantine) son lógica pura sin dependencia de
 * Mantine — se REUSAN tal cual, no se duplican, para no arriesgar drift
 * entre dos catálogos de comandos.
 *
 * Búsqueda simple `.includes()` (no fuzzy) sobre label+descripción+
 * keywords · igual que el original.
 */
export function CommandPalette() {
  const navigate = useNavigate();
  const userRoles = useUserRoles();
  const [abierto, setAbierto] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [indiceActivo, setIndiceActivo] = useState(0);

  useCommandPaletteShortcut(() => {
    setAbierto((v) => !v);
    setFiltro("");
    setIndiceActivo(0);
  });

  const userPermisos = useUserPermisos();
  const disponibles = useMemo(() => comandosDisponiblesPara(COMANDOS, userPermisos), [userPermisos]);
  const filtrados = useMemo(() => filtrarPorTexto(disponibles, filtro), [disponibles, filtro]);

  function ejecutar(c: Comando) {
    setAbierto(false);
    void navigate({ to: c.href as never });
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndiceActivo((i) => Math.min(i + 1, filtrados.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndiceActivo((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const item = filtrados[indiceActivo];
      if (item) ejecutar(item);
    }
  }

  if (userRoles.length === 0) return null;

  return (
    <Dialog open={abierto} onOpenChange={setAbierto}>
      <DialogContent showClose={false} className="top-[20vh] max-w-lg -translate-y-0 overflow-hidden p-0">
        <div className="flex items-center gap-2.5 border-b border-border px-4 py-3.5">
          <IconSearch size={16} stroke={1.7} className="shrink-0 text-muted-foreground" />
          <input
            autoFocus
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setIndiceActivo(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Buscar comando…  (↑ ↓ Enter para navegar)"
            className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="max-h-90 overflow-y-auto py-1">
          {filtrados.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Sin coincidencias.</p>
          ) : (
            <div className="flex flex-col">
              {filtrados.map((c, idx) => (
                <CommandPaletteItem key={c.id} comando={c} activo={idx === indiceActivo} onHover={() => setIndiceActivo(idx)} onClick={() => ejecutar(c)} />
              ))}
            </div>
          )}
        </div>
        <CommandPaletteFooter />
      </DialogContent>
    </Dialog>
  );
}
